import { daemonServiceLaneSchema } from "../daemon/service-contract.js";
import { computerFileAccessModeSchema } from "../daemon/computer-file-access.js";
import { prepareReadinessRuntimeIdSchema } from "./prepare-readiness.js";
import { currentLocalRuntimeReplyReadinessResponseSchema, localRuntimeConnectionStatusSchema, localRuntimeDeviceTypeSchema, localRuntimeExecutionTargetStatusSchema, localRuntimePlatformFamilySchema } from "./local-runtimes.js";
import { organizationComputerLocalAgentDetailsSchema, projectOrganizationComputerLocalAgentDetails } from "./organization-computers.js";
import * as z$1 from "zod/v4";
//#region src/schemas/http/owner-computer-diagnostics.ts
const boundedDiagnosticCodeSchema = z$1.string().trim().min(1).max(256);
const boundedDiagnosticMessageSchema = z$1.string().trim().min(1).max(1024);
const computerFileAccessSummarySchema = z$1.discriminatedUnion("status", [
	z$1.strictObject({
		deviceId: z$1.string().trim().min(1).max(512),
		folderCount: z$1.number().int().nonnegative(),
		mode: computerFileAccessModeSchema,
		revision: z$1.number().int().positive(),
		status: z$1.literal("available"),
		updatedAtMs: z$1.number().int().nonnegative()
	}),
	z$1.strictObject({ status: z$1.literal("unsupported") }),
	z$1.strictObject({
		code: boundedDiagnosticCodeSchema,
		message: boundedDiagnosticMessageSchema,
		status: z$1.literal("unavailable")
	})
]);
const ownerComputerLocalRuntimeCapabilitySchema = z$1.strictObject({
	agentControllerRef: boundedDiagnosticCodeSchema,
	details: organizationComputerLocalAgentDetailsSchema.nullable().optional(),
	displayName: z$1.string().trim().min(1).max(256).optional(),
	enabled: z$1.boolean(),
	reasonCodes: z$1.array(boundedDiagnosticCodeSchema),
	status: z$1.enum([
		"ready",
		"not_ready",
		"stale",
		"unknown"
	])
});
const ownerComputerLocalRuntimeExecutionTargetSchema = z$1.strictObject({
	capabilities: z$1.array(ownerComputerLocalRuntimeCapabilitySchema),
	deviceId: z$1.string().trim().min(1).max(256),
	isCurrent: z$1.boolean(),
	lastObservedAtMs: z$1.number().int().nonnegative().nullable(),
	openMeldServiceRuntimeId: prepareReadinessRuntimeIdSchema.nullable(),
	reasonCodes: z$1.array(boundedDiagnosticCodeSchema),
	runtimeLane: daemonServiceLaneSchema,
	status: localRuntimeExecutionTargetStatusSchema
});
const ownerComputerLocalRuntimeSchema = z$1.strictObject({
	computerFileAccess: z$1.strictObject({
		readPolicy: z$1.boolean(),
		updatePolicy: z$1.boolean()
	}).optional(),
	connectedComputerId: z$1.string().trim().min(1).max(256).optional(),
	connectionStatus: localRuntimeConnectionStatusSchema.optional(),
	deviceDisplayName: z$1.string().trim().min(1).max(256),
	deviceId: z$1.string().trim().min(1).max(256),
	deviceType: localRuntimeDeviceTypeSchema,
	executionTargets: z$1.array(ownerComputerLocalRuntimeExecutionTargetSchema),
	firstSeenAtMs: z$1.number().int().nonnegative(),
	isCurrent: z$1.boolean(),
	lastObservedAtMs: z$1.number().int().nonnegative().nullable(),
	osVersion: z$1.string().trim().min(1).max(120).nullable(),
	platformFamily: localRuntimePlatformFamilySchema
});
const ownerComputerLocalRuntimesSchema = z$1.strictObject({
	currentConnectedComputerId: z$1.string().trim().min(1).max(256).nullable().optional(),
	currentDeviceId: z$1.string().trim().min(1).max(256).nullable(),
	currentRuntimeLane: daemonServiceLaneSchema.nullable(),
	localRuntimes: z$1.array(ownerComputerLocalRuntimeSchema)
});
const ownerComputerDiagnosticsSchema = z$1.strictObject({
	currentComputerFileAccess: computerFileAccessSummarySchema,
	currentLocalRuntimeReplyReadiness: currentLocalRuntimeReplyReadinessResponseSchema,
	localRuntimes: ownerComputerLocalRuntimesSchema
});
function projectOwnerComputerLocalRuntimes(response) {
	return ownerComputerLocalRuntimesSchema.parse({
		...response.currentConnectedComputerId === void 0 ? {} : { currentConnectedComputerId: response.currentConnectedComputerId },
		currentDeviceId: response.currentDeviceId,
		currentRuntimeLane: response.currentRuntimeLane,
		localRuntimes: response.localRuntimes.map((computer) => ({
			...computer.computerFileAccess ? { computerFileAccess: computer.computerFileAccess } : {},
			...computer.connectedComputerId ? { connectedComputerId: computer.connectedComputerId } : {},
			...computer.connectionStatus ? { connectionStatus: computer.connectionStatus } : {},
			deviceDisplayName: computer.deviceDisplayName,
			deviceId: computer.deviceId,
			deviceType: computer.deviceType,
			executionTargets: computer.executionTargets.map((target) => ({
				capabilities: target.capabilities.map((capability) => {
					const snapshot = capability.agentControllerInterface;
					return {
						agentControllerRef: capability.agentControllerRef,
						...snapshot === void 0 ? {} : { details: snapshot === null ? null : projectOrganizationComputerLocalAgentDetails(snapshot) },
						...snapshot ? { displayName: snapshot.displayName } : {},
						enabled: capability.enabled,
						reasonCodes: capability.reasonCodes,
						status: capability.status
					};
				}),
				deviceId: target.deviceId,
				isCurrent: target.isCurrent,
				lastObservedAtMs: target.lastObservedAtMs,
				openMeldServiceRuntimeId: target.openMeldServiceRuntimeId,
				reasonCodes: target.reasonCodes,
				runtimeLane: target.runtimeLane,
				status: target.status
			})),
			firstSeenAtMs: computer.firstSeenAtMs,
			isCurrent: computer.isCurrent,
			lastObservedAtMs: computer.lastObservedAtMs,
			osVersion: computer.osVersion,
			platformFamily: computer.platformFamily
		}))
	});
}
function summarizeComputerFileAccessPolicy(response) {
	return {
		deviceId: response.deviceId,
		folderCount: "folders" in response.policy ? response.policy.folders.length : 0,
		mode: response.policy.mode,
		revision: response.policy.revision,
		status: "available",
		updatedAtMs: response.policy.updatedAtMs
	};
}
//#endregion
export { computerFileAccessSummarySchema, ownerComputerDiagnosticsSchema, ownerComputerLocalRuntimesSchema, projectOwnerComputerLocalRuntimes, summarizeComputerFileAccessPolicy };

//# sourceMappingURL=owner-computer-diagnostics.js.map